// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getReorderPadDataViewProperty } from "@keller/App/Store/Selectors/ReorderPadSelectors";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ReorderPadProductCountStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    totalItemCount: getReorderPadDataViewProperty(state, "pagination")?.totalItemCount,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ReorderPadProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
}

export const productCountStyles: ReorderPadProductCountStyles = {
    wrapper: {
        css: css`
            margin-bottom: 20px;
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
            }
        `,
    },
    countText: {
        size: 20,
        weight: "bold",
        css: css`
            white-space: nowrap;
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
    itemsText: {
        size: 20,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
};

const ReorderPadProductCount: FC<Props> = ({ totalItemCount, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(productCountStyles, extendedStyles));
    const itemsText = totalItemCount && totalItemCount > 1 ? " Products" : " Product";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.countText} data-test-selector="productListCountText">
                {totalItemCount}
            </Typography>
            <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ReorderPadProductCount);
